import { Layout, Menu, Dropdown, Button } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  ShoppingCartOutlined,
  MenuOutlined,
  ApartmentOutlined,
  SolutionOutlined,
  BarChartOutlined,
  DollarCircleOutlined,
  ReloadOutlined,
  FileTextOutlined,
  SettingOutlined,
  CreditCardOutlined,
  BankOutlined,
  BookOutlined,
  ProfileOutlined,
  HistoryOutlined,
  SearchOutlined,
  RiseOutlined,
  InboxOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import logo from "../../assets/branding/android-chrome-192x192.png";
import { useAuth } from "../../store/AuthContext";
import { PERMS } from "../../constants/permissions";
import styles from './Sidebar.module.css';

const { Sider } = Layout;

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const { hasPermission, hasAnyPermission } = useAuth();

  /**
   * A parent entry is only worth showing when at least one of its children
   * survived the permission filter — an empty submenu is a dead end.
   */
  const submenu = (parent: any, children: any[]) => {
    const visible = children.filter(Boolean);
    return visible.length > 0 ? { ...parent, children: visible } : false;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSelect = ({ key }: { key: string }) => {
    navigate(key);
  };

  // Every entry is gated on the permission its screen actually needs, so the
  // menu never offers a page the API will answer with 403.
  const rawMenuItems = [
    // Dashboard
    hasPermission(PERMS.dashboard.view) && {
      key: "/dashboard",
      icon: <DashboardOutlined />,
      label: t("dashboard")
    },

    // Report
    submenu(
      {
        key: "/report",
        icon: <BarChartOutlined />,
        label: t("report"),
      },
      [
        hasPermission(PERMS.report.itemReport) && { key: "/report/item-report", icon: <SearchOutlined />, label: t("item_report") },
        hasPermission(PERMS.report.bestSelling) && { key: "/report/best-selling", icon: <RiseOutlined />, label: t("best_selling_report") },
        hasPermission(PERMS.report.mostReturned) && { key: "/report/most-returned", icon: <ReloadOutlined />, label: t("most_returned_report") },
        hasPermission(PERMS.report.topItems) && { key: "/report/top-items", icon: <ShoppingCartOutlined />, label: t("top_items") },
        hasPermission(PERMS.report.topCustomers) && { key: "/report/top-customers", icon: <TeamOutlined />, label: t("top_customers") },
        hasPermission(PERMS.report.topSuppliers) && { key: "/report/top-suppliers", icon: <BankOutlined />, label: t("top_suppliers") },
        hasPermission(PERMS.report.yearlySales) && { key: "/report/yearly-sales", icon: <BarChartOutlined />, label: t("yearly_sales_chart") },
        hasPermission(PERMS.report.dailySummary) && { key: "/report/daily-summary", icon: <FileTextOutlined />, label: t("daily_summary") },
        hasPermission(PERMS.report.stockReport) && { key: "/report/stock-report", icon: <BarChartOutlined />, label: t("stock_report") },
        hasPermission(PERMS.report.stockValueReport) && { key: "/report/product-stock-value", icon: <BookOutlined />, label: t("product_stock_value_report") },
        hasPermission(PERMS.report.profitReport) && { key: "/report/profit-report", icon: <DollarCircleOutlined />, label: t("profit_report") },
      ]
    ),

    // Customers
    hasPermission(PERMS.customer.list) && {
      key: "/customers",
      icon: <TeamOutlined />,
      label: t("customers")
    },

    // Products / Inventory
    hasPermission(PERMS.product.list) && {
      key: "/products",
      icon: <BookOutlined />,
      label: t("products")
    },
    hasPermission(PERMS.supplier.list) && {
      key: "/suppliers",
      icon: <BankOutlined />,
      label: t("suppliers")
    },

    // Sales
    submenu(
      { key: "/sales", icon: <SolutionOutlined />, label: t("sales") },
      [
        hasPermission(PERMS.sell.create) && { key: "/sales/form", icon: <ProfileOutlined />, label: t("add_sale") },
        hasPermission(PERMS.sell.list) && { key: "/sales/list", icon: <FileTextOutlined />, label: t("sale_list") },
      ]
    ),

    // Purchases
    submenu(
      { key: "/purchase", icon: <ShoppingCartOutlined />, label: t("purchase") },
      [
        hasPermission(PERMS.purchase.create) && { key: "/purchase/form", icon: <ProfileOutlined />, label: t("add_purchase") },
        hasPermission(PERMS.purchase.list) && { key: "/purchase/list", icon: <FileTextOutlined />, label: t("purchase_list") },
      ]
    ),

    // Loan Repayments (from customers)
    submenu(
      { key: "/loan-repayments", icon: <CreditCardOutlined />, label: t("loan_repayments") },
      [
        hasPermission(PERMS.loanRepayment.create) && { key: "/loan-repayments/form", icon: <ProfileOutlined />, label: t("add_loan_repayment") },
        hasPermission(PERMS.loanRepayment.list) && { key: "/loan-repayments/list", icon: <FileTextOutlined />, label: t("loan_repayment_list") },
      ]
    ),

    // Supplier Payments (to suppliers)
    submenu(
      { key: "/supplier-payments", icon: <DollarCircleOutlined />, label: t("supplier_payments") },
      [
        hasPermission(PERMS.supplierPayment.create) && { key: "/supplier-payments/form", icon: <ProfileOutlined />, label: t("add_supplier_payment") },
        hasPermission(PERMS.supplierPayment.list) && { key: "/supplier-payments/list", icon: <FileTextOutlined />, label: t("supplier_payment_list") },
      ]
    ),

    // Expenses
    submenu(
      { key: "/expenses", icon: <DollarCircleOutlined />, label: t("expenses") },
      [
        hasPermission(PERMS.expense.create) && { key: "/expenses/form", icon: <ProfileOutlined />, label: t("add_expense") },
        hasPermission(PERMS.expense.list) && { key: "/expenses/list", icon: <FileTextOutlined />, label: t("expense_list") },
      ]
    ),

    // Returns
    submenu(
      { key: "/returns", icon: <ReloadOutlined />, label: t("returns") },
      [
        hasPermission(PERMS.returnInvoice.create) && { key: "/returns/form", icon: <ProfileOutlined />, label: t("add_return") },
        hasPermission(PERMS.returnInvoice.list) && { key: "/returns/list", icon: <FileTextOutlined />, label: t("return_list") },
      ]
    ),

    // Preferences Group (Roles, Users, City, Currency, Settings)
    submenu(
      { key: "Preferences", icon: <SettingOutlined />, label: t("Preferences") },
      [
        hasAnyPermission(PERMS.role.list, PERMS.dashboard.roles) && {
          key: "/roles",
          icon: <TeamOutlined />,
          label: t("roles")
        },
        hasAnyPermission(PERMS.user.list, PERMS.dashboard.users) && {
          key: "/users",
          icon: <UserOutlined />,
          label: t("users")
        },
        hasAnyPermission(PERMS.city.list, PERMS.dashboard.city) && {
          key: "/city",
          icon: <ApartmentOutlined />,
          label: t("city")
        },
        hasPermission(PERMS.currency.list) && {
          key: "/currency",
          icon: <DollarCircleOutlined />,
          label: t("currencies")
        },
        hasAnyPermission(PERMS.audit.list, PERMS.audit.read) && {
          key: "/audit-logs",
          icon: <HistoryOutlined />,
          label: t("audit_logs")
        },
        hasAnyPermission(PERMS.archive.list, PERMS.archive.read) && {
          key: "/archive",
          icon: <InboxOutlined />,
          label: t("archive")
        },
        hasAnyPermission(PERMS.settings.read, PERMS.settings.backup) && {
          key: "/settings",
          icon: <SettingOutlined />,
          label: t("settings")
        }
      ]
    ),

    // Profile (always last)
    {
      key: "/profile",
      icon: <UserOutlined />,
      label: t("profile")
    }
  ].filter(Boolean);

  const dropdownMenu = (
    <Menu
      onClick={handleSelect}
      items={rawMenuItems as any}
      className={styles.menuCustom}
    />
  );

  return isMobile ? (
    <div
      style={{
        padding: "8px 16px",
        display: "flex",
        alignItems: "center",
        borderRadius: "14px",
        margin: "10px",
        boxShadow: "0 4px 16px 0 rgba(39, 21, 107, 0.15)",
        zIndex: 1200,
      }}
    >
      <div
        style={{
          cursor: "pointer",
          display: "inline-block"
        }}
        onClick={() => navigate("/dashboard")}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            navigate("/dashboard");
          }
        }}
        tabIndex={0}
        role="button"
        aria-label="Go to dashboard"
      >
        <img
          src={logo}
          alt="Logo"
          style={{
            height: 40,
            marginRight: 14,
            borderRadius: 10,
            boxShadow: "0 2px 8px #1a0d3a25"
          }}
        />
      </div>
      <Dropdown
        overlay={dropdownMenu}
        trigger={["click"]}
        getPopupContainer={triggerNode =>
          (triggerNode.parentNode as HTMLElement) || document.body
        }
      >
        <Button
          icon={<MenuOutlined />}
          style={{
            color: "#fff",
            background: "#22314A",
            borderRadius: "8px",
            fontWeight: "bold",
            border: "none",
            boxShadow: "0 1px 6px #00A09D25"
          }}
        >
          {t("menu")}
        </Button>
      </Dropdown>
    </div>

  ) : (
    <Sider width={210} className={styles.siderCustom}>
        <div
          className={styles.logoContainer}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/dashboard")}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              navigate("/dashboard");
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Go to dashboard"
        >
        <img src={logo} alt="Logo" style={{ height: 58, borderRadius: 14, boxShadow: "0 4px 24px #DF551540" }} />
      </div>
      <Menu
        mode="inline"
        theme="dark"
        defaultSelectedKeys={["/dashboard"]}
        onClick={handleSelect}
        items={rawMenuItems as any}
        className={styles.menuCustom}
        style={{
          fontSize: "16px",
          fontWeight: 500,
          background: "22314A"
        }}
      />
    </Sider>
  );
};

export default Sidebar;
